import React, {useEffect, useRef, useState} from 'react';
import {useMutation, useQuery, useQueryClient} from '@tanstack/react-query';
import Button from '@/components/ui/Button';
import LoadingStatus from '@/components/ui/LoadingStatus';
import {playersV2API, SquadReviewAction, SquadReviewPlayer, SquadSyncResult} from '@/features/players/api';
import {latestSquadSyncQuery, playerKeys, squadReviewQuery} from '@/features/players/queries';
import {SquadStatusFilter} from '@/features/players/types';
import {getV2Error} from '@/services/api/v2';
import SquadReviewDialog from './SquadReviewDialog';

export interface SquadReviewOpenRequest {
  id: number;
  afterDataUpdatedAt: number;
}

interface SquadSyncPanelProps {
  workspaceId: string;
  squadStatus: SquadStatusFilter;
  onSquadStatusChange: (status: SquadStatusFilter) => void;
  syncFeedback?: {type: 'pending' | 'success' | 'error'; message: string};
  latestOperation?: 'sync' | 'decision';
  onDecisionStart?: () => void;
  reviewOpenRequest?: SquadReviewOpenRequest;
  onReviewClose: () => void;
}

export const syncResultLabel = (result: SquadSyncResult): string =>
  `${result.added} added, ${result.updated} updated, ${result.reactivated} reactivated, ${result.review} for review, ${result.ignored} ignored, ${result.unchanged} unchanged.`;

const plural = (count: number, singular: string): string => `${count} ${count === 1 ? singular : `${singular}s`}`;

const SquadSyncPanel: React.FC<SquadSyncPanelProps> = ({workspaceId, squadStatus, onSquadStatusChange, syncFeedback, latestOperation, onDecisionStart, reviewOpenRequest, onReviewClose}) => {
  const queryClient = useQueryClient();
  const [selected, setSelected] = useState<Set<string>>(() => new Set());
  const [isReviewOpen, setReviewOpen] = useState(false);
  const [announcement, setAnnouncement] = useState('');
  const [decisionStarted, setDecisionStarted] = useState(false);
  const previousReviewIdsRef = useRef<Set<string>>(new Set());
  const handledReviewOpenRequestRef = useRef(0);
  const latest = useQuery({...latestSquadSyncQuery(workspaceId), enabled: Boolean(workspaceId)});
  const review = useQuery({...squadReviewQuery(workspaceId), enabled: Boolean(workspaceId)});
  const hasWorkflowData = latest.data !== undefined || review.data !== undefined;

  useEffect(() => {
    if (!review.data) return;
    const reviewIds = new Set<string>(review.data.map((player: SquadReviewPlayer) => player.id));
    const previousIds = previousReviewIdsRef.current;
    setSelected(current => {
      const next = new Set(Array.from(current).filter(playerId => reviewIds.has(playerId)));
      reviewIds.forEach(playerId => {
        if (!previousIds.has(playerId)) next.add(playerId);
      });
      return next;
    });
    previousReviewIdsRef.current = reviewIds;
  }, [review.data]);

  useEffect(() => {
    if (!reviewOpenRequest
      || reviewOpenRequest.id <= handledReviewOpenRequestRef.current
      || review.dataUpdatedAt <= reviewOpenRequest.afterDataUpdatedAt) return;
    handledReviewOpenRequestRef.current = reviewOpenRequest.id;
    if (review.data?.length) setReviewOpen(true);
  }, [review.data, review.dataUpdatedAt, reviewOpenRequest]);

  const invalidateSquad = async () => {
    await Promise.all([
      queryClient.invalidateQueries({queryKey: playerKeys.all(workspaceId)}),
      queryClient.invalidateQueries({queryKey: playerKeys.latestSquadSync(workspaceId)}),
      queryClient.invalidateQueries({queryKey: playerKeys.squadReview(workspaceId)}),
    ]);
  };

  const decision = useMutation({
    mutationFn: ({action, playerIds}: {action: SquadReviewAction; playerIds: string[]}) =>
      playersV2API.decideSquadReview(action, playerIds, workspaceId),
    onMutate: () => {
      setDecisionStarted(true);
      setAnnouncement('');
      onDecisionStart?.();
    },
    onSuccess: (result, variables) => {
      setSelected(new Set());
      setReviewOpen(false);
      onReviewClose();
      setAnnouncement(variables.action === 'archive'
        ? `${plural(result.archived, 'player')} archived. History is retained.`
        : `${plural(result.keptActive, 'player')} kept current.`);
      void invalidateSquad();
    },
  });

  const decide = (action: SquadReviewAction) => {
    const playerIds = Array.from(selected);
    if (playerIds.length === 0 || decision.isPending) return;
    decision.mutate({action, playerIds});
  };
  const decisionFeedback = decision.isError
    ? {type: 'error' as const, message: getV2Error(decision.error).message}
    : announcement
      ? {type: 'success' as const, message: announcement}
      : undefined;
  const feedback = latestOperation === 'sync'
    ? syncFeedback
    : latestOperation === 'decision' || decisionStarted
      ? decisionFeedback
      : syncFeedback;

  return (
    <section aria-label="Wyscout squad workflow" className="rounded-xl bg-white p-4 shadow-sm ring-1 ring-gray-200 dark:bg-gray-900 dark:ring-gray-800">
      <div>
        <h2 className="text-lg font-bold text-gray-900 dark:text-gray-100">Wyscout squad review</h2>
        <p className="mt-1 text-sm text-gray-600 dark:text-gray-300">Review players missing from the current Wyscout squad.</p>
        {latest.data && (
          <p className="mt-2 text-sm text-gray-600 dark:text-gray-300">
            Latest sync: {latest.data.status}. {syncResultLabel(latest.data)}
          </p>
        )}
      </div>

      <div className="mt-4 flex flex-wrap gap-2" role="group" aria-label="Squad lifecycle">
        <Button size="sm" variant={squadStatus === 'current' ? 'primary' : 'outline'} aria-pressed={squadStatus === 'current'} onClick={() => onSquadStatusChange('current')}>Current players</Button>
        <Button size="sm" variant={squadStatus === 'archived' ? 'primary' : 'outline'} aria-pressed={squadStatus === 'archived'} onClick={() => onSquadStatusChange('archived')}>Archived players</Button>
      </div>

      {feedback && (
        <p
          className={`mt-3 text-sm ${feedback.type === 'error' ? 'text-danger-700 dark:text-danger-300' : 'text-gray-700 dark:text-gray-200'}`}
          role={feedback.type === 'error' ? 'alert' : 'status'}
          aria-label={latestOperation === 'sync'
            ? feedback.type === 'pending' ? 'Squad synchronization status' : feedback.type === 'success' ? 'Squad synchronization result' : undefined
            : undefined}
          aria-live="polite"
        >
          {feedback.message}
        </p>
      )}
      {(latest.isPending || review.isPending) && (
        <LoadingStatus label="Loading Wyscout squad review" className="mt-3" />
      )}
      {(latest.isError || review.isError) && (
        <div role="alert" className="mt-3 flex flex-wrap items-center gap-2 text-sm text-danger-700 dark:text-danger-300">
          <span>{hasWorkflowData ? 'Unable to refresh Wyscout squad review. Existing data is retained.' : 'Unable to load Wyscout squad review.'}</span>
          <Button size="sm" variant="outline" loading={latest.isFetching || review.isFetching} loadingLabel="Retrying Wyscout squad review" onClick={() => { void Promise.all([latest.refetch(), review.refetch()]); }}>Retry Wyscout squad review</Button>
        </div>
      )}

      {review.data && review.data.length > 0 && (
        <Button className="mt-4" size="sm" variant="outline" onClick={() => setReviewOpen(true)}>
          Review {plural(review.data.length, 'pending player')}
        </Button>
      )}
      <SquadReviewDialog
        isOpen={isReviewOpen}
        players={review.data ?? []}
        selected={selected}
        pendingAction={decision.isPending ? decision.variables?.action : undefined}
        errorMessage={decision.isError ? getV2Error(decision.error).message : undefined}
        onClose={() => {
          setReviewOpen(false);
          onReviewClose();
        }}
        onTogglePlayer={(playerId, checked) => setSelected(current => {
          const next = new Set(current);
          if (checked) next.add(playerId); else next.delete(playerId);
          return next;
        })}
        onToggleAll={checked => setSelected(checked ? new Set((review.data ?? []).map((player: SquadReviewPlayer) => player.id)) : new Set())}
        onKeepActive={() => decide('keep_active')}
        onArchive={() => decide('archive')}
      />
    </section>
  );
};

export default SquadSyncPanel;
